"use client";

import { c as _c } from "react-compiler-runtime";
import { X } from "lucide-react";
import Link from "next/link";
import { useState, useSyncExternalStore } from "react";
import { Image } from "@/components/primitives";
import awardTrophy from "./assets/award-trophy.webp";
import { isTechnologyInnovationAwardPublished } from "./technologyInnovationAward.constants";
export type AwardAnnouncementBarCopy = {
  message: string;
  ctaLabel: string;
  /** Shorter versions for phones. */
  shortMessage: string;
  shortCtaLabel: string;
  dismissLabel: string;
};
type AwardAnnouncementBarProps = {
  /** Locale-prefixed path of the award post. */
  href: string;
  /** Translated on the server (AwardAnnouncementBarContainer). */
  copy: AwardAnnouncementBarCopy;
};

// The home page is prebuilt, so the embargo is checked in the browser instead: the
// server snapshot (and hydration) always says unpublished, and the client re-renders
// with the visitor's clock. Nothing to subscribe to; a reload after 9:00 shows the bar.
const subscribeToNothing = () => () => {};
const getIsPublished = () => isTechnologyInnovationAwardPublished(new Date());
const getIsPublishedOnServer = () => false;

/**
 * Mocha bar pinned above the site header, open on every page load. While it is mounted,
 * globals.css grows `--announcement-bar-offset`, which pushes the page and the fixed
 * header down; dismissing unmounts it for the rest of the page view.
 */
const AwardAnnouncementBar = t0 => {
  const $ = _c(29);
  const {
    href,
    copy
  } = t0;
  const isPublished = useSyncExternalStore(subscribeToNothing, getIsPublished, getIsPublishedOnServer);
  const [isDismissed, setIsDismissed] = useState(false);
  if (!isPublished || isDismissed) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image alt="" className="h-16 w-auto shrink-0" loading="eager" sizes="40px" src={awardTrophy} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== copy.shortMessage) {
    t2 = <span className="md:hidden">{copy.shortMessage}</span>;
    $[1] = copy.shortMessage;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== copy.message) {
    t3 = <span className="hidden md:inline">{copy.message}</span>;
    $[3] = copy.message;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== copy.shortCtaLabel) {
    t4 = <span className="md:hidden">{copy.shortCtaLabel}</span>;
    $[5] = copy.shortCtaLabel;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== copy.ctaLabel) {
    t5 = <span className="hidden md:inline">{copy.ctaLabel}</span>;
    $[7] = copy.ctaLabel;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <span className="text-amber group-hover:text-champagne-white whitespace-nowrap underline underline-offset-4 transition-colors">{t4}{t5}</span>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t6) {
    t7 = <span className="type-body-sm text-champagne-white text-pretty">{t2}{t3}{" "}{t6}</span>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== href || $[17] !== t7) {
    t8 = <Link className="group mr-12 flex min-w-0 items-center gap-4 md:mr-10" href={href}>{t1}{t7}</Link>;
    $[16] = href;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsDismissed(true);
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <X aria-hidden="true" className="size-4" />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== copy.dismissLabel) {
    t11 = <button aria-label={copy.dismissLabel} className="text-champagne-white/60 hover:text-champagne-white absolute top-1/2 right-5 inline-flex size-9 -translate-y-1/2 items-center justify-center transition-colors md:right-0" onClick={t9} type="button">{t10}</button>;
    $[21] = copy.dismissLabel;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <div className="swiss-container relative flex h-full items-center justify-center">{t8}{t11}</div>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== copy.message || $[27] !== t12) {
    t13 = <aside aria-label={copy.message} className="bg-mocha fixed inset-x-0 top-[calc(var(--announcement-bar-offset)-var(--announcement-bar-height))] z-[75] h-(--announcement-bar-height) bg-[radial-gradient(ellipse_30%_140%_at_50%_50%,hsl(var(--brand-champagne-white)/0.18),transparent)] pt-[env(safe-area-inset-top)]" data-award-announcement-bar="">{t12}</aside>;
    $[26] = copy.message;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};
export default AwardAnnouncementBar;